import React, { type ReactNode, type RefObject } from 'react';
import { useIntl } from 'react-intl';
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md';
import { Button, IconButton, TOOLTIP_ALIGN_END } from '@aibuild/ui';
import type { FullscreenControls } from './useFullscreen';
import { AreaTitle, Area, ExitBar } from './FullscreenArea.styled';

interface FullscreenAreaProps {
  readonly areaRef: RefObject<HTMLDivElement | null>;
  readonly fullscreen: FullscreenControls;
  readonly title: string;
  readonly children: ReactNode;
}

/** The app and what it waits on the person for, shown alone on the screen when asked. */
const FullscreenArea: React.FC<FullscreenAreaProps> = ({ areaRef, fullscreen, title, children }) => {
  const intl = useIntl();
  return (
    <Area ref={areaRef} data-testid="app-fullscreen">
      {fullscreen.active && (
        <ExitBar>
          <AreaTitle>{title}</AreaTitle>
          <Button variant="text" size="sm" leadingIcon={<MdFullscreenExit size={18} />} onClick={fullscreen.exit} dataTestId="app-fullscreen__exit">
            {intl.formatMessage({ id: 'apps.fullscreen.exit', defaultMessage: 'Exit full screen' })}
          </Button>
        </ExitBar>
      )}
      {children}
    </Area>
  );
};

export const FullscreenButton: React.FC<{ readonly fullscreen: FullscreenControls }> = ({ fullscreen }) => {
  const intl = useIntl();
  if (!fullscreen.supported) return null;
  const label = fullscreen.active
    ? intl.formatMessage({ id: 'apps.fullscreen.exit', defaultMessage: 'Exit full screen' })
    : intl.formatMessage({ id: 'apps.fullscreen.enter', defaultMessage: 'Full screen' });
  return (
    <IconButton
      ariaLabel={label}
      tooltip={label}
      tooltipAlign={TOOLTIP_ALIGN_END}
      icon={fullscreen.active ? <MdFullscreenExit size={20} /> : <MdFullscreen size={20} />}
      variant="ghost"
      onClick={fullscreen.active ? fullscreen.exit : fullscreen.enter}
      dataTestId={fullscreen.active ? 'app-fullscreen__leave' : 'app-fullscreen__enter'}
    />
  );
};

export default FullscreenArea;
